/**
 * Swedish phone number check.
 *
 * One rule for the whole marketplace: every contact phone number we store must
 * be a Swedish number in E.164 form, i.e. `+46` followed by 7-9 digits.
 *
 * A CommonJS twin lives at server/api-util/phoneNumber.js for the Node server
 * (SMS sending, the one-off data fix script). Keep the two in sync: this file
 * is ESM/bundled, the server is CommonJS. If you change a rule here, change it
 * there and update both test files.
 */

/** The country code every stored number starts with. */
export const SWEDISH_PHONE_PREFIX = '+46';

// Characters people use to group digits. Parentheses are included because
// numbers copied from other systems often look like "(070) 123-4567".
const SEPARATORS = /[\s\-.()]/g;

// National form: trunk prefix "0" plus 7-9 digits, e.g. 0701234567, 08123456.
const NATIONAL_RE = /^0\d{7,9}$/;

// Stored form: +46 plus 7-9 digits, e.g. +46701234567.
const E164_RE = /^\+46\d{7,9}$/;

// Swedish mobile numbers are the "7" series and always have exactly 9 digits
// after the country code (07X XXX XX XX). The 7-9 digit range above exists for
// landlines, whose area codes vary in length. Letting that range apply to
// mobiles too would turn a mistyped number such as "07047447" into
// "+467047447": it looks correct, so nobody ever notices, but no call or SMS
// can reach it. A mobile with the wrong length is a typo, not a number.
const MOBILE_RE = /^\+467\d{8}$/;

const stripSeparators = value => String(value).replace(SEPARATORS, '');

/**
 * Rewrite a phone number to the stored Swedish E.164 form.
 *
 * Accepted, after spaces / dashes / dots / parentheses are removed:
 *   - `0XXXXXXX` .. `0XXXXXXXXX`   -> rewritten to `+46XXXXXXX...`
 *   - `+46XXXXXXX` .. `+46XXXXXXXXX` -> kept as is
 *   - `0046...` -> the `00` international prefix becomes `+`, then the rules
 *     above apply. This is the same Swedish number written a different way, so
 *     it is fixable rather than broken. Note that this does NOT re-open general
 *     international support: `001...` becomes `+1...` and is then rejected.
 *
 * Mobile numbers (the `07` / `+467` series) are held to one exact length: `+46`
 * plus 9 digits. See MOBILE_RE above for why.
 *
 * Anything else (missing digits, another country code, letters, a non-string)
 * returns null, which callers must treat as "reject".
 *
 * @param {String} value phone number as the user typed it or as stored
 * @returns {String|null} the `+46...` number, or null when it cannot be fixed
 */
export const normalizeSwedishPhoneNumber = value => {
  if (typeof value !== 'string') {
    return null;
  }

  let cleaned = stripSeparators(value);

  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}`;
  }

  let e164 = null;
  if (NATIONAL_RE.test(cleaned)) {
    // Drop the trunk prefix "0": it is replaced by the country code.
    e164 = `${SWEDISH_PHONE_PREFIX}${cleaned.slice(1)}`;
  } else if (E164_RE.test(cleaned)) {
    e164 = cleaned;
  }

  if (e164 === null) {
    return null;
  }

  const isMobile = e164.startsWith(`${SWEDISH_PHONE_PREFIX}7`);
  return isMobile && !MOBILE_RE.test(e164) ? null : e164;
};

/** True when `value` is a Swedish number we can store. */
export const isSwedishPhoneNumber = value => normalizeSwedishPhoneNumber(value) !== null;

/**
 * True when the field holds no number at all. The sign-up form pre-fills the
 * field with "+46", so a bare prefix has to count as empty — otherwise an
 * optional phone field could never be left blank.
 */
export const isBlankPhoneNumber = value => {
  if (typeof value !== 'string') {
    return !value;
  }
  const cleaned = stripSeparators(value);
  return cleaned === '' || cleaned === SWEDISH_PHONE_PREFIX;
};

/**
 * Return `values` with each named phone field rewritten to the stored `+46...`
 * form.
 *
 * Used by the form submit handlers so that what the user typed ("070-123 45
 * 67") is saved in one shape ("+46701234567"). A blank field drops the key, so
 * nothing is stored. A value that cannot be normalized is passed through
 * untouched — the field validator blocks submit before that can happen, and
 * silently dropping someone's number would be worse than storing it.
 *
 * `keys` exists because not every form calls the field `phoneNumber`: the
 * Adresser form also holds `whatsAppNumber`, and the checkout calls its field
 * `recipientPhoneNumber`. Normalizing all of them through one function keeps the
 * rules in a single place — the server's one-off data fix already treats
 * whatsAppNumber as an ordinary contact number, so the entry forms have to
 * agree with it or the two will drift apart again.
 *
 * @param {Object} values form values
 * @param {Array<String>} keys which fields hold a phone number
 * @returns {Object} values with those fields normalized
 */
export const withNormalizedPhoneNumber = (values, keys = ['phoneNumber']) => {
  if (!values) {
    return values;
  }

  return keys.reduce((acc, key) => {
    if (typeof acc[key] === 'undefined') {
      return acc;
    }

    if (isBlankPhoneNumber(acc[key])) {
      const { [key]: _blank, ...rest } = acc;
      return rest;
    }

    const normalized = normalizeSwedishPhoneNumber(acc[key]);
    return normalized ? { ...acc, [key]: normalized } : acc;
  }, values);
};
